import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, faExclamationTriangle, faCheckCircle, faClipboardList, faBoxes,
  faClock, faArrowRight, faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { loadPortalDashboard } from '../../services/supplierApi';
import { useToast } from '../../components/ui/ToastProvider';
import SupplierNavbar from '../../components/supplier/SupplierNavbar';
import SupplierArticleDetailModal from '../../components/supplier/SupplierArticleDetailModal';

interface QuestionnaireItem {
  id: string;
  title: string;
  type: string;
  status: 'completed' | 'in_progress' | 'draft' | 'overdue' | 'sent';
  deadline: string;
  progress: number;
  accessToken: string;
  sentAt?: string;
  completedAt?: string;
  canAccess: boolean;
}

interface Article {
  _id: string;
  articleNumber: string;
  externalArticleNumber?: string;
  supplierAssignedId?: string;
  name: string;
  description?: string;
  brand?: string;
  content?: string;
  contentUnit?: string;
  status: 'draft' | 'requested' | 'in_progress' | 'submitted' | 'approved' | 'completed' | 'rejected' | 'needs_correction';
  updatedAt?: string;
  createdAt?: string;
}

interface DashboardStats {
  questionnaires: {
    total: number;
    completed: number;
    pending: number;
    inProgress: number;
  };
  articles: {
    total: number;
    approved: number;
    pending: number;
    needsCorrection: number;
    draft: number;
  };
}

const SupplierPortalDashboard: React.FC = () => {
  const { masterToken } = useParams<{ masterToken: string }>();
  const navigate = useNavigate();
  const { showError } = useToast();

  // Core States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [supplierInfo, setSupplierInfo] = useState<any>(null);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireItem[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    questionnaires: { total: 0, completed: 0, pending: 0, inProgress: 0 },
    articles: { total: 0, approved: 0, pending: 0, needsCorrection: 0, draft: 0 }
  });

  // Modal States
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [showArticleModal, setShowArticleModal] = useState(false);

  // Data Loading
  const loadDashboardData = useCallback(async () => {
    if (!masterToken) {
      setError('Ungültiger Portal-Link');
      setLoading(false);
      return;
    }

    console.log('📊 Starting loadDashboardData for masterToken:', masterToken);

    // **PREVENT RAPID SUCCESSIVE CALLS - but allow supplier switches**
    const lastLoadTime = sessionStorage.getItem('lastDashboardLoad');
    const lastLoadedSupplier = sessionStorage.getItem('lastLoadedSupplier');
    const currentSupplierContext = sessionStorage.getItem('activeSupplierContext');
    const currentSupplierName = currentSupplierContext ? JSON.parse(currentSupplierContext).name : null;
    
    const now = Date.now();
    const isSupplierSwitch = lastLoadedSupplier !== currentSupplierName;
    
    if (lastLoadTime && now - parseInt(lastLoadTime) < 2000 && !isSupplierSwitch) {
      console.log('⏰ Preventing rapid dashboard reload (2s cooldown)');
      return;
    }
    
    // **TRACK CURRENT SUPPLIER FOR SWITCH DETECTION**
    sessionStorage.setItem('lastDashboardLoad', now.toString());
    if (currentSupplierName) {
      sessionStorage.setItem('lastLoadedSupplier', currentSupplierName);
      console.log('🔄 Loading dashboard for supplier:', currentSupplierName);
    }

    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      const storedSessionToken = sessionStorage.getItem('supplierPortalSession');
      if (!storedSessionToken) {
        // **PREVENT INFINITE REDIRECT LOOP WITH ENHANCED COOLDOWN**
        const lastRedirect = sessionStorage.getItem('lastLoginRedirect');
        const lastRedirectTime = parseInt(sessionStorage.getItem('lastLoginRedirectTime') || '0');
        const currentPath = `/supplier/portal/${masterToken}/login`;
        const cooldownPeriod = 15000; // Increased to 15 seconds to break the loop
        
        if (lastRedirect === currentPath && Date.now() - lastRedirectTime < cooldownPeriod) {
          console.error(`🚫 Preventing infinite redirect loop to login page (cooldown: ${cooldownPeriod/1000}s)`);
          setError('Anmeldefehler - bitte gehen Sie direkt zur Login-Seite und versuchen Sie es erneut');
          setLoading(false);
          return;
        }
        
        console.log(`🔄 No session token found, redirecting to login with enhanced cooldown protection`);
        sessionStorage.setItem('lastLoginRedirect', currentPath);
        sessionStorage.setItem('lastLoginRedirectTime', Date.now().toString());
        
        // Clear any existing portal session data to force fresh login
        sessionStorage.removeItem('supplierPortalSessionTime');
        sessionStorage.removeItem('supplierInfo');
        sessionStorage.removeItem('activeSupplierContext');
        
        // Add a longer delay before redirect to prevent rapid fire
        setTimeout(() => {
          navigate(`/supplier/portal/${masterToken}/login`, { replace: true });
        }, 500);
        return;
      }

      console.log('📊 Calling loadPortalDashboard API...');
      const data = await loadPortalDashboard(masterToken, storedSessionToken);
      console.log('✅ Dashboard data received:', data);
      
      // Process questionnaire data
      const questionnairesWithAccess = data.questionnaires.map((q: any) => ({
        ...q,
        canAccess: (q as any).canAccess ?? true
      }));
      
      setQuestionnaires(questionnairesWithAccess);
      setArticles(data.articles || []);
      setSupplierInfo(data.supplier);
      
      // **IMPORTANT: Update supplierInfo in session storage for other components**
      if (data.supplier) {
        sessionStorage.setItem('supplierInfo', JSON.stringify(data.supplier));
        console.log('💾 Updated supplierInfo in session storage:', {
          name: data.supplier.name,
          kreditorNumber: data.supplier.kreditorNumber,
          hasKreditorNumber: !!data.supplier.kreditorNumber
        });
      }
      
      // Calculate stats
      const questionnaireStats = {
        total: questionnairesWithAccess.length,
        completed: questionnairesWithAccess.filter((q: any) => q.status === 'completed').length,
        pending: questionnairesWithAccess.filter((q: any) => q.status === 'sent').length,
        inProgress: questionnairesWithAccess.filter((q: any) => q.status === 'in_progress').length
      };

      const articleStats = {
        total: (data.articles || []).length,
        approved: (data.articles || []).filter((a: any) => a.status === 'approved').length,
        pending: (data.articles || []).filter((a: any) => ['requested', 'submitted', 'in_progress'].includes(a.status)).length,
        needsCorrection: (data.articles || []).filter((a: any) => a.status === 'needs_correction').length,
        draft: (data.articles || []).filter((a: any) => a.status === 'draft').length
      };

      setStats({
        questionnaires: questionnaireStats,
        articles: articleStats
      });

      // **CLEAR REDIRECT LOOP PROTECTION ON SUCCESSFUL LOAD**
      sessionStorage.removeItem('lastLoginRedirect');
      sessionStorage.removeItem('lastLoginRedirectTime');

      // Handle password change requirements
      const requirePasswordChange = sessionStorage.getItem('requirePasswordChange') === 'true';
      const isFirstLogin = sessionStorage.getItem('isFirstLogin') === 'true';
      const skipPasswordCheck = sessionStorage.getItem('skipPasswordCheck') === 'true';
      const isOnChangePasswordPage = window.location.pathname.includes('/change-password');
      
      if (!skipPasswordCheck && !isOnChangePasswordPage && 
          (requirePasswordChange || isFirstLogin || data.supplier?.mustChangePassword || data.supplier?.isFirstLogin)) {
        sessionStorage.setItem('passwordChangeRedirect', `/supplier/portal/${masterToken}`);
        navigate(`/supplier/portal/${masterToken}/change-password`, { replace: true });
        return;
      }
      
      if (!skipPasswordCheck) {
        sessionStorage.removeItem('requirePasswordChange');
        sessionStorage.removeItem('isFirstLogin');
      }
      sessionStorage.removeItem('skipPasswordCheck');
      
      console.log('✅ Dashboard loaded successfully');
      
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      
      // **ENHANCED ERROR HANDLING FOR AUTHENTICATION ISSUES**
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      if (errorMessage === 'REDIRECT_TO_LOGIN') {
        console.log('🔐 Explicit redirect to login required');
        sessionStorage.removeItem('supplierPortalSession');
        sessionStorage.removeItem('supplierPortalSessionTime');
        sessionStorage.removeItem('supplierInfo');
        navigate(`/supplier/portal/${masterToken}/login`, { replace: true });
        return;
      }
      
      if (errorMessage.includes('401') || 
          errorMessage.includes('Unauthorized') || 
          errorMessage.includes('Token') ||
          errorMessage.includes('Session') ||
          errorMessage.includes('Authentication')) {
        console.log('🔐 Authentication error detected, clearing session and redirecting to login');
        sessionStorage.removeItem('supplierPortalSession');
        sessionStorage.removeItem('supplierPortalSessionTime');
        sessionStorage.removeItem('supplierInfo');
        navigate(`/supplier/portal/${masterToken}/login`, { replace: true });
        return;
      }
      
      // **FOR RATE LIMITING - SHOW FRIENDLY MESSAGE**
      if (errorMessage.includes('Zu viele Anfragen') || errorMessage.includes('warten')) {
        setError('Das System ist momentan überlastet. Bitte warten Sie einen Moment und laden Sie die Seite neu.');
        showError('System überlastet', errorMessage);
      } else {
        // **FALLBACK: If dashboard loading fails without clear session token, redirect to login**
        const hasValidSession = sessionStorage.getItem('supplierPortalSession');
        if (!hasValidSession) {
          console.log('🔄 No valid session found after error, redirecting to login');
          navigate(`/supplier/portal/${masterToken}/login`, { replace: true });
          return;
        }
        
        setError('Dashboard-Daten konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
        showError('Fehler beim Laden der Dashboard-Daten');
      }
    } finally {
      console.log('📊 Setting loading to false');
      setLoading(false);
    }
  }, [masterToken, navigate, showError]);

  // **EFFECT THAT RUNS ON MOUNT AND WHEN MASTERTOKEN CHANGES**
  useEffect(() => {
    if (masterToken) {
      console.log('🚀 Initiating dashboard data load for masterToken:', masterToken);
      loadDashboardData();
    }
  }, [masterToken, loadDashboardData]); // Include loadDashboardData in dependencies

  // **NEW: React to supplier context changes (for supplier switching)**
  useEffect(() => {
    const handleStorageChange = () => {
      const activeContext = sessionStorage.getItem('activeSupplierContext');
      if (activeContext && masterToken) {
        try {
          const context = JSON.parse(activeContext);
          console.log('🔄 [DASHBOARD] Supplier context changed, reloading data for:', context.name);
          
          // **FORCE RELOAD by clearing cache**
          sessionStorage.removeItem('lastDashboardLoad');
          sessionStorage.removeItem('lastLoadedSupplier');
          
          setTimeout(() => {
            loadDashboardData();
          }, 200); // Small delay to ensure context is fully updated
        } catch (e) {
          console.log('❌ [DASHBOARD] Error parsing supplier context');
        }
      }
    };

    // Listen for storage changes
    window.addEventListener('storage', handleStorageChange);
    
    // **ALSO: Watch for manual context changes via custom event**
    window.addEventListener('supplierContextChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('supplierContextChanged', handleStorageChange);
    };
  }, [masterToken, loadDashboardData]);

  // Get pending questionnaires (only sent and in_progress)
  const getPendingQuestionnaires = () => {
    return questionnaires.filter(q => ['sent', 'in_progress'].includes(q.status));
  };

  // Get pending articles (only requested, submitted, in_progress, needs_correction)
  const getPendingArticles = () => {
    return articles.filter(a => ['requested', 'submitted', 'in_progress', 'needs_correction'].includes(a.status));
  };

  // Status Helper Functions
  const getStatusColor = (status: string, type: 'questionnaire' | 'article') => {
    if (type === 'questionnaire') {
      switch (status) {
        case 'completed': return 'bg-green-100 text-green-800';
        case 'in_progress': return 'bg-blue-100 text-blue-800';
        case 'sent': return 'bg-orange-100 text-orange-800';
        case 'overdue': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    } else {
      switch (status) {
        case 'approved': return 'bg-green-100 text-green-800';
        case 'submitted': return 'bg-blue-100 text-blue-800';
        case 'requested': return 'bg-orange-100 text-orange-800';
        case 'in_progress': return 'bg-blue-100 text-blue-800';
        case 'needs_correction': return 'bg-red-100 text-red-800';
        case 'rejected': return 'bg-red-100 text-red-800';
        case 'draft': return 'bg-gray-100 text-gray-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }
  };

  const getStatusLabel = (status: string, type: 'questionnaire' | 'article') => {
    if (type === 'questionnaire') {
      switch (status) {
        case 'completed': return 'Abgeschlossen';
        case 'in_progress': return 'In Bearbeitung';
        case 'sent': return 'Ausstehend';
        case 'overdue': return 'Überfällig';
        default: return 'Entwurf';
      }
    } else {
      switch (status) {
        case 'approved': return 'Genehmigt';
        case 'submitted': return 'Eingereicht';
        case 'requested': return 'Angefordert';
        case 'in_progress': return 'In Bearbeitung';
        case 'needs_correction': return 'Korrektur erforderlich';
        case 'rejected': return 'Abgelehnt';
        case 'draft': return 'Entwurf';
        default: return status;
      }
    }
  };

  const getStatusIcon = (status: string, type: 'questionnaire' | 'article') => {
    if (type === 'questionnaire') {
      switch (status) {
        case 'completed': return faCheckCircle;
        case 'in_progress': return faClock;
        case 'overdue': return faExclamationTriangle;
        default: return faClipboardList;
      }
    } else {
      switch (status) {
        case 'approved': return faCheckCircle;
        case 'submitted': return faCheckCircle;
        case 'requested': return faClock;
        case 'in_progress': return faClock;
        case 'needs_correction': return faExclamationTriangle;
        case 'rejected': return faExclamationTriangle;
        default: return faBoxes;
      }
    }
  };

  // Event Handlers
  const handleOpenQuestionnaire = (questionnaire: QuestionnaireItem) => {
    if (!questionnaire.canAccess) {
      showError('Dieser Fragebogen ist nicht verfügbar.');
      return;
    }

    console.log('📋 Opening questionnaire:', questionnaire.id, questionnaire.accessToken);
    
    // **ERSTELLE SESSION TOKEN FÜR FRAGEBOGEN**
    const sessionToken = sessionStorage.getItem('supplierPortalSession');
    if (!sessionToken) {
      showError('Keine gültige Session gefunden. Bitte melden Sie sich erneut an.');
      navigate(`/supplier/portal/${masterToken}/login`);
      return;
    }
    
    // **SETZE FRAGEBOGEN SESSION TOKEN**
    // Der Fragebogen erwartet einen 'supplierSessionToken' und 'supplierSessionExpiry'
    const expiryTime = Date.now() + (24 * 60 * 60 * 1000); // 24 Stunden
    sessionStorage.setItem('supplierSessionToken', sessionToken);
    sessionStorage.setItem('supplierSessionExpiry', expiryTime.toString());
    sessionStorage.setItem('fromSupplierLogin', 'true');
    sessionStorage.setItem('supplierPortalMasterToken', masterToken!); // **WICHTIG: masterToken für Portal API speichern**
    
    // **NAVIGATE TO STANDARD QUESTIONNAIRE ROUTE**
    const questionnaireUrl = `/questionnaire/${questionnaire.accessToken}`;
    console.log('🧭 Navigating to questionnaire URL:', questionnaireUrl);
    console.log('🔐 Session token set for questionnaire access');
    console.log('🏠 Portal masterToken saved for API access:', masterToken);
    
    // **VERWENDE NAVIGATE STATT WINDOW.LOCATION.HREF**
    navigate(questionnaireUrl);
  };

  const handleViewArticle = (article: Article) => {
    setSelectedArticle(article);
    setShowArticleModal(true);
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SupplierNavbar 
          supplierName="Laden..." 
          masterToken={masterToken} 
          pendingQuestionnaires={0}
          totalArticles={0}
        />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="text-6xl text-red-500 mb-4 animate-spin" />
            <p className="text-xl text-gray-600">Dashboard wird geladen...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SupplierNavbar 
          supplierName="Fehler" 
          masterToken={masterToken}
          pendingQuestionnaires={0}
          totalArticles={0}
        />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center max-w-md mx-auto bg-white rounded-xl shadow-lg p-8">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-6xl text-red-500 mb-6" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Fehler beim Laden</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/supplier/portal')}
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Zur Anmeldung
            </button>
          </div>
        </div>
      </div>
    );
  }

  const pendingQuestionnaires = getPendingQuestionnaires();
  const pendingArticles = getPendingArticles();

  return (
    <div className="min-h-screen bg-gray-50">
      <SupplierNavbar 
        supplierName={supplierInfo?.name || 'Lieferant'} 
        masterToken={masterToken}
        pendingQuestionnaires={stats.questionnaires.pending + stats.questionnaires.inProgress}
        totalArticles={stats.articles.total}
      />
      
      {/* Volle Breite Container */}
      <div className="w-full px-6 py-6">
        {/* Compact Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-1">
                  Willkommen, {supplierInfo?.name || 'Lieferant'}
                </h1>
                <p className="text-gray-300">
                  Übersicht Ihrer Fragebögen und Artikel
                </p>
              </div>
              {(pendingQuestionnaires.length > 0 || pendingArticles.length > 0) && (
                <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold">{pendingQuestionnaires.length + pendingArticles.length}</div>
                  <div className="text-sm text-gray-300">
                    Benötigen Bearbeitung
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Statistics Grid - volle Breite */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8"
        >
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">Fragebögen Gesamt</h3>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.questionnaires.total}
                </div>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <FontAwesomeIcon icon={faClipboardList} className="text-blue-600 text-lg" />
              </div>
            </div>
            <div className="mt-2 flex items-center text-sm">
              <span className="text-green-600 font-medium">{stats.questionnaires.completed} abgeschlossen</span>
              {stats.questionnaires.pending > 0 && (
                <>
                  <span className="text-gray-400 mx-2">•</span>
                  <span className="text-orange-600 font-medium">{stats.questionnaires.pending} ausstehend</span>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">Artikel Gesamt</h3>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.articles.total}
                </div>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <FontAwesomeIcon icon={faBoxes} className="text-purple-600 text-lg" />
              </div>
            </div>
            <div className="mt-2 flex items-center text-sm">
              <span className="text-green-600 font-medium">{stats.articles.approved} genehmigt</span>
              {stats.articles.pending > 0 && (
                <>
                  <span className="text-gray-400 mx-2">•</span>
                  <span className="text-orange-600 font-medium">{stats.articles.pending} ausstehend</span>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">In Bearbeitung</h3>
                <div className="text-2xl font-bold text-blue-600 mt-1">
                  {stats.questionnaires.inProgress + stats.articles.pending}
                </div>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <FontAwesomeIcon icon={faClock} className="text-blue-600 text-lg" />
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              {stats.questionnaires.inProgress} Fragebögen, {stats.articles.pending} Artikel
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">Korrekturen</h3>
                <div className="text-2xl font-bold text-red-600 mt-1">
                  {stats.articles.needsCorrection}
                </div>
              </div>
              <div className="p-2 bg-red-100 rounded-lg">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-lg" />
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              Artikel benötigen Korrektur
            </div>
          </div>

          {/* Zusätzliche Statistiken für volle Breite */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">Entwürfe</h3>
                <div className="text-2xl font-bold text-gray-600 mt-1">
                  {stats.articles.draft}
                </div>
              </div>
              <div className="p-2 bg-gray-100 rounded-lg">
                <FontAwesomeIcon icon={faBoxes} className="text-gray-600 text-lg" />
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              Artikel als Entwurf
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600">Fortschritt</h3>
                <div className="text-2xl font-bold text-green-600 mt-1">
                  {stats.questionnaires.total > 0 
                    ? Math.round((stats.questionnaires.completed / stats.questionnaires.total) * 100)
                    : 0
                  }%
                </div>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-lg" />
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              Fragebögen abgeschlossen
            </div>
          </div>
        </motion.div>

        {/* Content Grid - volle Breite */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Pending Questionnaires */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              <FontAwesomeIcon icon={faClipboardList} className="mr-2 text-blue-600" />
              Aktuelle Fragebögen
            </h3>
            <div className="space-y-3">
              {pendingQuestionnaires.length > 0 ? (
                pendingQuestionnaires.slice(0, 5).map((questionnaire) => (
                  <div
                    key={questionnaire.id}
                    onClick={() => handleOpenQuestionnaire(questionnaire)}
                    className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <div className="flex items-center">
                      <div className="p-2 bg-white rounded-lg mr-3">
                        <FontAwesomeIcon 
                          icon={getStatusIcon(questionnaire.status, 'questionnaire')}
                          className="text-blue-600"
                        />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{questionnaire.title}</div>
                        <div className="text-sm text-gray-600">
                          {getStatusLabel(questionnaire.status, 'questionnaire')}
                        </div>
                      </div>
                    </div>
                    <FontAwesomeIcon icon={faArrowRight} className="text-gray-400" />
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-300 mb-3" />
                  <p>Alle Fragebögen abgeschlossen</p>
                </div>
              )}
              {pendingQuestionnaires.length > 5 && (
                <button
                  onClick={() => navigate(`/supplier/portal/${masterToken}/questionnaires`)}
                  className="w-full mt-4 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  Alle {pendingQuestionnaires.length} Fragebögen anzeigen
                </button>
              )}
            </div>
          </motion.div>

          {/* Pending Articles */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              <FontAwesomeIcon icon={faBoxes} className="mr-2 text-purple-600" />
              Aktuelle Artikel
            </h3>
            <div className="space-y-3">
              {pendingArticles.length > 0 ? (
                pendingArticles.slice(0, 5).map((article) => (
                  <div
                    key={article._id}
                    onClick={() => handleViewArticle(article)}
                    className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <div className="flex items-center">
                      <div className="p-2 bg-white rounded-lg mr-3">
                        <FontAwesomeIcon 
                          icon={getStatusIcon(article.status, 'article')}
                          className="text-purple-600"
                        />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{article.name}</div>
                        <div className="text-sm text-gray-600">
                          {getStatusLabel(article.status, 'article')}
                        </div>
                      </div>
                    </div>
                    <FontAwesomeIcon icon={faArrowRight} className="text-gray-400" />
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-300 mb-3" />
                  <p>Alle Artikel bearbeitet</p>
                </div>
              )}
              {pendingArticles.length > 5 && (
                <button
                  onClick={() => navigate(`/supplier/portal/${masterToken}/articles`)}
                  className="w-full mt-4 px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  Alle {pendingArticles.length} Artikel anzeigen
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Article Detail Modal */}
      {selectedArticle && (
        <SupplierArticleDetailModal
          isOpen={showArticleModal}
          onClose={() => {
            setShowArticleModal(false);
            setSelectedArticle(null);
          }}
          article={selectedArticle}
          onSave={(updatedArticle) => {
            setArticles(prev => prev.map(a => a._id === updatedArticle._id ? updatedArticle : a));
            setSelectedArticle(updatedArticle);
          }}
        />
      )}
    </div>
  );
};

export default SupplierPortalDashboard;